import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Smartphone, BookOpen } from "lucide-react";
import { SHOP_ALL_URL, ALPHAPULSE_URL, NUTRISPECS_URL, ABOUT_URL } from "@/data/products";
import { AppStoreButtons } from "./AppStoreButtons";

const links = [
  { label: "Home", href: "/", type: "route" },
  { label: "Why Us", href: "/#benefits", type: "hash" },
  { label: "Journal", href: "/journal", type: "route" },
  { label: "Reviews", href: "/#testimonials", type: "hash" },
  { label: "About", href: ABOUT_URL, type: "external" },
  { label: "AlphaPulse Fitness App", href: ALPHAPULSE_URL, type: "external" },
  { label: "NutriSpecs: Food Scanner", href: NUTRISPECS_URL, type: "external" },
];

const linkClass =
  "relative whitespace-nowrap text-sm text-[#1a1f1b]/80 hover:text-[#1a1f1b] transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#e85d04] hover:after:w-full after:transition-[width] after:duration-300";

const NavItem = ({ l, onClick, className }) => {
  const testid = `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
  if (l.type === "route")
    return (
      <Link to={l.href} onClick={onClick} data-testid={testid} className={className}>
        {l.label}
      </Link>
    );
  const ext = l.type === "external" ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <a href={l.href} onClick={onClick} data-testid={testid} className={className} {...ext}>
      {l.label}
    </a>
  );
};

const CATALOG_PDF = `${process.env.REACT_APP_BACKEND_URL}/api/catalog.pdf`;

export const Navbar = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    navigate("/");
    if (window.lenis && typeof window.lenis.scrollTo === "function") {
      window.lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  };
  const [banner, setBanner] = useState(() =>
    typeof window === "undefined" ? true : localStorage.getItem("ao_app_banner") !== "dismissed"
  );
  const bannerRef = useRef(null);

  useLayoutEffect(() => {
    if (!banner) {
      document.documentElement.style.setProperty("--ann-h", "0px");
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const setH = () =>
      requestAnimationFrame(() =>
        document.documentElement.style.setProperty("--ann-h", `${el.offsetHeight}px`)
      );
    setH();
    const ro = new ResizeObserver(setH);
    ro.observe(el);
    return () => ro.disconnect();
  }, [banner]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const dismissBanner = () => {
    localStorage.setItem("ao_app_banner", "dismissed");
    setBanner(false);
  };

  const mobileMenu = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-[#1d231e] text-[#f4f1eb] p-6 xl:hidden overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="flex justify-between items-center">
            <Link to="/" onClick={goHome} aria-label="Home">
              <img src="/logo-light.png" alt="Alpha Omega Vitality" className="h-10 w-auto" />
            </Link>
            <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
              <X size={28} />
            </button>
          </div>
          <div className="mt-14 flex flex-col gap-5">
            {links.map((l) => (
              <NavItem
                key={l.label}
                l={l}
                onClick={() => setOpen(false)}
                className="font-serif-display text-4xl tracking-tight"
              />
            ))}
            <a
              href={CATALOG_PDF}
              download="Alpha-Omega-Vitality-Catalog.pdf"
              onClick={() => setOpen(false)}
              data-testid="mobile-catalog-btn"
              className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/30 px-8 py-3 text-lg"
            >
              <BookOpen size={18} /> Download our Catalog
            </a>
            <a
              href={SHOP_ALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-shop-btn"
              className="inline-flex w-fit rounded-full bg-[#e85d04] px-8 py-3 text-lg"
            >
              Shop All
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <a
        href="#top"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:rounded-full focus:bg-[#1a1f1b] focus:px-4 focus:py-2 focus:text-sm focus:text-white"
      >
        Skip to content
      </a>

      {/* Download app announcement bar */}
      {banner && (
        <div
          ref={bannerRef}
          data-testid="app-banner"
          className="fixed top-0 left-0 right-0 z-[70] bg-[#12160f] text-[#f4f1eb]"
        >
          <div className="relative mx-auto flex max-w-[1400px] flex-col items-center justify-center gap-x-6 gap-y-3 px-6 py-3 text-center sm:flex-row sm:text-left lg:px-10">
            <div className="flex items-center gap-2.5 pr-6 sm:pr-0">
              <Smartphone size={18} className="shrink-0 text-[#c79a3e]" />
              <p className="text-xs sm:text-sm">
                <span className="font-medium">Take Alpha Omega Vitality with you</span>
                <span className="hidden text-[#f4f1eb]/60 md:inline">
                  {" "}— personalized wellness, supplement tracking & exclusive offers.
                </span>
              </p>
            </div>
            <AppStoreButtons compact data-testid="banner-app-buttons" />
            <button
              onClick={dismissBanner}
              data-testid="app-banner-dismiss"
              aria-label="Dismiss"
              className="absolute right-3 top-3 text-[#f4f1eb]/60 transition-colors duration-300 hover:text-[#f4f1eb] sm:top-1/2 sm:-translate-y-1/2"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        data-testid="main-navbar"
        style={{ top: "var(--ann-h, 0px)" }}
        className={`fixed left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
          scrolled
            ? "bg-[#f4f1eb] backdrop-blur-xl border-b border-[#1a1f1b]/10 shadow-[0_2px_20px_rgba(26,31,27,0.07)] py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link to="/" onClick={goHome} data-testid="nav-logo" className="flex items-center">
            <img
              src="/logo-dark.png"
              alt="Alpha Omega Vitality"
              className={`w-auto transition-[height] duration-500 ${scrolled ? "h-9" : "h-11"}`}
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
            {links.map((l) => (
              <NavItem key={l.label} l={l} className={linkClass} />
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CATALOG_PDF}
              download="Alpha-Omega-Vitality-Catalog.pdf"
              data-testid="nav-catalog-btn"
              className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#1a1f1b]/25 px-5 py-2.5 text-sm text-[#1a1f1b] hover:border-[#e85d04] hover:text-[#b34700] transition-colors duration-300"
            >
              <BookOpen size={15} /> <span><span className="xl:hidden 2xl:inline">Download our </span>Catalog</span>
            </a>
            <a
              href={SHOP_ALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-shop-btn"
              className="hidden sm:inline-flex items-center rounded-full bg-[#e85d04] text-[#f4f1eb] text-sm px-6 py-2.5 hover:bg-[#b34700] transition-colors duration-300"
            >
              Shop All
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              className="xl:hidden text-[#1a1f1b]"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>
      {typeof document !== "undefined" && createPortal(mobileMenu, document.body)}
    </>
  );
};
